import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, PricingTable } from "@clerk/react";
import { isAdminUser } from "../data/adminUsers";

const FEATURES = [
  {
    icon: "⚡",
    title: "Live Speed",
    desc: "Knot-by-knot speed data for every ship at sea, live-tracked from AIS data.",
  },
  {
    icon: "⛽",
    title: "Fuel Burn",
    desc: "Real fuel consumption in metric tons per hour for 200+ vessels.",
  },
  {
    icon: "💰",
    title: "Fuel Cost",
    desc: "Estimated fuel cost per hour and annual fuel bill for every ship.",
  },
  {
    icon: "🏁",
    title: "Race Mode",
    desc: "The top 10 fastest cruise ships at sea right now, live leaderboard.",
  },
  {
    icon: "🚢",
    title: "Full Fleet",
    desc: "Every ship sortable by speed, fuel burn, cost, and destination.",
  },
  {
    icon: "📍",
    title: "Live Positions",
    desc: "Track any ship's exact position, heading, and next port of call.",
  },
];

export default function Captain() {
  const { isSignedIn, isLoaded, has, userId } = useAuth();
  const navigate = useNavigate();
  const isCaptain = isLoaded && (has({ plan: "vessel_vibes_captain" }) || isAdminUser(userId));

  // Redirect existing Captains away from this page
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && isCaptain) {
      navigate("/race");
    }
  }, [isLoaded, isSignedIn, isCaptain, navigate]);

  if (!isLoaded) return <div className="min-h-screen bg-[#080e1a]" />;

  return (
    <div className="min-h-screen bg-[#080e1a] text-white pt-16">

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid md:grid-cols-2 gap-12 items-center">

        {/* Left: copy */}
        <div>
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/25 rounded-full px-3 py-1 mb-6">
            <span className="text-cyan-400 text-xs font-semibold tracking-wider uppercase">Captain Access</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black leading-tight mb-4">
            What Is Your Ship<br />
            <span className="text-cyan-400">Actually Burning?</span>
          </h1>
          <p className="text-gray-400 text-lg mb-8 leading-relaxed">
            Real-time speed, fuel consumption, and fuel cost for 200+ cruise ships worldwide — live-tracked from AIS data.
          </p>
          <div className="space-y-3">
            {[
              "See which ships are burning millions in fuel",
              "Track the top 10 fastest ships at sea right now",
              "Full fleet data: speed, burn rate, annual fuel bill",
            ].map((point) => (
              <div key={point} className="flex items-start gap-3">
                <span className="text-cyan-400 mt-0.5 shrink-0">✓</span>
                <span className="text-gray-300 text-sm">{point}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: pricing table */}
        <div className="bg-white rounded-2xl p-6 text-gray-900 shadow-2xl shadow-cyan-500/10">
          <PricingTable newSubscriptionRedirectUrl="/race?upgraded=1" />
        </div>
      </div>

      {/* ── Stats bar ────────────────────────────────────── */}
      <div className="border-y border-white/5 bg-white/3">
        <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-2 gap-4 text-center">
          {[
            { stat: "200+", label: "Ships tracked" },
            { stat: "Live", label: "AIS data" },
          ].map(({ stat, label }) => (
            <div key={label}>
              <div className="text-2xl font-black text-cyan-400">{stat}</div>
              <div className="text-xs text-gray-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features grid ────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-center mb-2">Everything included</h2>
        <p className="text-gray-500 text-sm text-center mb-10">One subscription. Full access. No tiers.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white/4 border border-white/8 rounded-xl p-5 hover:border-cyan-500/25 transition-colors">
              <div className="text-2xl mb-3">{f.icon}</div>
              <div className="font-semibold text-white mb-1.5">{f.title}</div>
              <div className="text-gray-500 text-sm leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom CTA ───────────────────────────────────── */}
      <div className="border-t border-white/5">
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-black mb-3">Ready to see everything?</h2>
          <p className="text-gray-500 text-sm">Join other cruise fans tracking the world's biggest ships in real time.</p>
        </div>
      </div>

    </div>
  );
}
